<template>
  <div>
    <reports-title
      ref="reportsTitle"
      :title="pageTitle"
    />
    <reports-toolbar
      ref="reportsToolbar"
      :active-report-view-mode="reportViewMode"
      :drop-menu="modelsOfCalculationsMenu"
      :report-title="pageTitle"
      :range-value="toolbarRange"
      :toolbar-disabled="isStatic || isDisabled"
      @onSelectDropdown="onChangeCalculationMode"
      @onSelectReportViewMode="onSelectReportViewMode"
      @onClickExport="onExport"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
      :style="{ height: contentHeight }"
    >
      <template #body>
        <line-and-bar-chart
          v-if="reportViewMode === 'graphic'"
          :stub-config="reportChartConfigs.stub"
          :is-static="true"
          :custom-legends="true"
          :show-stub-message="false"
        />
        <skeletonTable v-if="reportViewMode === 'table'" />
      </template>
    </skeleton-wrapper>
    <div v-else>
      <line-and-bar-chart
        v-if="reportViewMode === 'graphic'"
        id="report_export_id"
        ref="chart"
        :config="chartConfig"
        :stub-config="reportChartConfigs.stub"
        :is-static="isStatic"
        :custom-legends="true"
        @toggleSeries="toggleSeries"
        @onAllLegendsDisabled="flag => isDisabled = flag"
      />
      <report-table
        v-if="reportViewMode === 'table'"
        ref="table"
        :config="tableConfig"
        :rows-data="tableData"
        :grouping="[]"
        :skin="'horizontal-borders'"
        :local-storage-table-id="currentFilter.type"
        :style="{ height: contentHeight }"
      />
    </div>
  </div>
</template>

<script>
import app from '$$app';
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import lineAndBarChart from '../include/charts/lineAndBarChart.vue';
import constants from '$$helpers/constants';
import timeReportConfigs from '../time/configCharts';
import budgetReportConfigs from '../budget/configCharts';
import MonthTimeReport from '../time/controllerReportByMonth';
import MonthBudgetReport from '../budget/controllerReportByMonth';
import reportConstants from '../include/constants';
import reportHelpers from '../include/helpers';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import statusHelper from '../../../helpers/status';
import reportTable from '../include/table/table.vue';
import budgetTableConfigs from '../budget/configTable';
import timeTableConfigs from '../time/configTable';
import skeletonTable from '../include/table/skeleton.vue';
import Export from '../include/export/export';
import Filter from '../../filter/filter';
import chartsHelpers from '../include/charts/helpers';
import tableHelpers from '../include/table/helpers';

export default {
  name: 'ReportByMonthMainPage',
  components: {
    reportsTitle, reportsToolbar, lineAndBarChart, skeletonWrapper, reportTable, skeletonTable,
  },
  props: {
    reportType: { type: String, required: true, default: 'budget' },
  },
  data() {
    return {
      locale: __,
      appListeners: [],
      projectId: null,
      baselines: [],
      baselineData: {},
      baselineId: null,
      chartConfig: {},
      tableData: [],
      reportViewMode: '',
      calculationMode: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
      toolbarRange: '',
      isStatic: true,
      isShowSkeleton: true,
      isDisabled: false,
      contentHeight: '0px',
    };
  },
  computed: {
    pageTitle() {
      if (this.$route?.name === 'timeReports') {
        return reportConstants.TITLE.time.timeByMonth;
      }
      if (this.$route?.name === 'budgetReports') {
        return reportConstants.TITLE.budget.budgetByMonth;
      }

      return '';
    },
    modelsOfCalculationsMenu() {
      return reportHelpers.getModelsOfCalculationsMenu(this.isStatic, this.calculationMode, this.baselines, this.baselineId);
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    allTasks() {
      return this.$store.getters['tasksModel/getTasksForGantt'](this.projectId).data;
    },
    baselineTasks() {
      if (this.baselineData?.tasks) {
        const baselineTasks = this.baselineData.tasks;

        return baselineTasks;
      }

      return [];
    },
    reportChartConfigs() {
      let configs = {};

      if (this.reportType === 'budget') {
        configs = budgetReportConfigs.monthReport(this.calculationMode);
      }

      if (this.reportType === 'time') {
        configs = timeReportConfigs.monthReport(this.calculationMode);
      }

      return configs;
    },
    tableConfig() {
      let config = null;

      if (this.reportType === 'budget') {
        config = budgetTableConfigs.budgetByMonth(this.calculationMode);
      }

      if (this.reportType === 'time') {
        config = timeTableConfigs.timeByMonth(this.calculationMode);
      }

      if (config) {
        return tableHelpers.getColumnVisibilityByCalculationMode(config, this.calculationMode);
      }

      return {
        columns: [],
        state: {},
      };
    },
    baselineEnabledByCalculationMode() {
      const baselineEnabledByCalculationMode = [
        constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE, constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE
      ].includes(this.calculationMode)

      return baselineEnabledByCalculationMode;
    }
  },
  watch: {
    'currentFilter.properties': {
      handler(newProps, oldProps) {
        if (oldProps === undefined) return;

        this.calculateReportByFilter(newProps, oldProps)
      },
      deep: true,
      immediate: true,
    },
    leftSidebarWidth(width) {
      this.clientWidth = `${document.documentElement.clientWidth - width}px`;
    },
    reportViewMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          mode: newVal,
        },
      });
    },
    calculationMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          calculationModel: newVal,
        },
      });
    },
  },
  created() {
    this.init();
  },
  beforeUnmount() {
    this.appListeners.forEach(event => app.off(event));
    window.removeEventListener('resize', this.calcContentHeight);
  },
  mounted() {
    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      calculation_mode: this.calculationMode,
    });

    this.appListeners.push(app.on('onAfterMonthTimeReportCalculation', result => this.setReportResult(result)));
    this.appListeners.push(app.on('onAfterMonthBubgetCalculation', result => this.setReportResult(result)));
    this.calcContentHeight();
    window.addEventListener('resize', this.calcContentHeight);
  },
  methods: {
    calcContentHeight() {
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight || 0;

      const titleRef = this.$refs.reportsTitle;
      const toolbarRef = this.$refs.reportsToolbar;

      const titleEl = titleRef && (titleRef.$el || titleRef);
      const toolbarEl = toolbarRef && (toolbarRef.$el || toolbarRef);

      const titleHeight = titleEl?.getBoundingClientRect().height || 0;
      const toolbarHeight = toolbarEl?.getBoundingClientRect().height || 0;

      const height = Math.max(viewportHeight - titleHeight - toolbarHeight, 0);

      this.contentHeight = `${height}px`;
    },
    init() {
      // this.chartConfig = { ...this.stubConfig };
      this.reportViewMode = this.$route?.query?.mode || 'graphic';
      this.calculationMode = this.$route?.query?.calculationModel || constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;

      if (this.reportType === 'budget') {
        MonthBudgetReport.initCostWorker();
      }

      if (this.reportType === 'time') {
        MonthTimeReport.initTimeWorker();
      }

      this.calculateReportByFilter(this.currentFilter.properties)
    },

    async calculateReportByFilter(newProps, oldProps = null) {
      Filter.watchChangedFilterProps(newProps, oldProps, {
        gantt_id: this.setProjectId,
        rangeDate: this.setRange,
      });

      if (this.projectId) {
        await this.getBaselines();
        this.updateFilterUIAndCalculationMode();
        this.calculateReportByWorker();
      } else {
        this.baselineId = null;
        this.baselines = [];
        this.baselineData = {};
        this.tableData = [];
        this.isStatic = true;
        this.isShowSkeleton = false;

        this.updateFilterAdvancedSettings();

        const chartComponent = this.$refs.chart;

        if (chartComponent && chartComponent.instance) {
          chartComponent.instance.dispatchAction({
            type: 'legendAllSelect',
          });
        }
      }
    },

    async setProjectId(ids) {
      if (ids?.length) {
        this.projectId = ids[0];
      } else {
        this.projectId = null;
      }
    },

    setRange() {
      this.toolbarRange = reportHelpers.setToolbarRangeValue(this.currentFilter);
    },

    calculateReportByWorker() {
      if (this.reportType === 'budget') {
        MonthBudgetReport.calculateCosts(this.allTasks, this.baselineTasks, this.calculationMode);
      }

      if (this.reportType === 'time') {
        MonthTimeReport.calculateTime(this.allTasks, this.baselineTasks, this.calculationMode);
      }
    },

    recalculateChart() {
      if (this.reportType === 'budget') {
        MonthBudgetReport.calculationMode = this.calculationMode;
        MonthBudgetReport.calculateMonthBudgetReport();
      }

      if (this.reportType === 'time') {
        MonthTimeReport.calculationMode = this.calculationMode;
        MonthTimeReport.calculateMonthTimeLogReport();
      }
    },

    async getBaselines() {
      this.baselineId = null;
      this.baselines = [];
      this.baselineData = {};

      await this.$store.dispatch('baselines/fetchBaselines', this.projectId);

      const baselines = this.$store.getters['baselines/getProjectBaselines'](this.projectId)
        .map(item => ({ ...item, created_at: new Date(item.created_at) }))
        .sort((a, b) => a.created_at - b.created_at);

      if (baselines?.length) {
        this.baselines = baselines;
        this.baselineId = baselines[baselines.length - 1].id;
        await this.getBaselineData(this.baselineId);
      }
    },

    async getBaselineData(baselineId) {
      const baseline = this.baselines.find(b => b.id === +baselineId);

      if (!baseline) {
        this.baselineData = {};

        return
      }

      const data = await this.$store.dispatch('baselines/getBaselineData', { projectId: baseline.gantt_id, eventId: baseline.event_id });

      if (data) {
        this.baselineData = data;
        this.baselineId = baselineId;
      }
    },

    setReportResult(result) {
      if (result) {
        this.chartConfig = result.chart;
        this.tableData = result.table;
        this.isStatic = false;
      } else {
        this.baselineId = null;
        this.baselines = [];
        this.baselineData = {};
        this.tableData = [];
        this.isStatic = true;
      }

      setTimeout(() => {
        this.isShowSkeleton = false;
      }, 500)
      statusHelper.handlers.hideMainSpinner();
    },

    async onChangeCalculationMode(selected) {
      statusHelper.handlers.showMainSpinner();
      const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

      userExtAnalytics.log('report_calculation_mode_change', {
        report_type: reportNameAnalytics,
        from: this.calculationMode,
        to: selected.type,
      });

      this.calculationMode = selected.type;

      let baselineEnabled = false;

      if (selected?.baselineId) {
        await this.getBaselineData(selected.baselineId);
        this.calculateReportByWorker();
        baselineEnabled = true;
      } else {
        this.baselineId = null;
        this.recalculateChart();
      }

      this.updateFilterAdvancedSettings({
        baselineEnabled: baselineEnabled,
      })
    },

    toggleSeries(chartInstance) {
      const options = chartInstance.getOption();
      const selected = options.legend[0].selected;
      let series = options.series;

      if (selected && Object.keys(selected).length) {
        series = series.filter(item => {
          if (!(item.name in selected)) {
            return item;
          }

          if (selected[item.name]) {
            return item;
          }
        });

        if (!series.length) {
          return;
        }
      }

      const leftValues = series.filter(item => item.type === 'bar').flatMap(item => item.data.map(obj => obj.value));
      const rightValues = series.filter(item => item.type === 'line').flatMap(item => item.data.map(obj => obj.value));
      const intervals = chartsHelpers.calculateYAxisIntervals(leftValues, rightValues);

      this.chartConfig = {
        ...this.chartConfig,
        yAxis: this.chartConfig.yAxis.map((axis, index) => {
          if (index === 0) {
            return { ...axis, interval: intervals.left };
          }
          if (index === 1) {
            return { ...axis, interval: intervals.right };
          }

          return axis;
        }),
      };
    },

    onSelectReportViewMode(data) {
      this.reportViewMode = data.type;
    },

    onExport() {
      const analyticTypeName = `${this.reportType}_variance_by_month`;
      const analyticTypeFormat = this.reportViewMode === 'table' ? 'XLSX' : 'PDF';

      if (this.reportViewMode === 'table') {
        Export.exportTanstackTableDataToExcel({
          title: __(`${this.pageTitle}`),
          tableInstance: this.$refs.table?.tableController?.tableInstance,
          reportType: this.reportType,
        });
      }

      if (this.reportViewMode === 'graphic') {
        Export.exportReportGraphicToPdf({
          title: __(`${this.pageTitle}`),
          range: this.toolbarRange,
        });
      }

      userExtAnalytics.log('report_export_done', {
        export_format: analyticTypeFormat,
        report_type: analyticTypeName,
      });
    },

    updateFilterUIAndCalculationMode() {
      if (!this.baselineEnabledByCalculationMode) {
        this.updateFilterAdvancedSettings();
      }

      if (this.baselineEnabledByCalculationMode && !this.baselines.length) {
        this.resetCalculationMode();
        this.updateFilterAdvancedSettings({
          baselineEnabled: false,
          isShowNotificationMessage: !!this.projectId,
        })
      }

      if (this.baselineEnabledByCalculationMode) {
        this.updateFilterAdvancedSettings({
          baselineEnabled: true,
          isShowNotificationMessage: false,
        })
      }
    },

    updateFilterAdvancedSettings(settings = {}) {
      const defaultFilterSettings = {
        type: this.currentFilter.type,
        settings: {
          baselineEnabled: false,
          isShowNotificationMessage: false,
        }
      }

      this.$store.commit('filter/updateAdvancedSettingsByFilterType', {
        ...defaultFilterSettings,
        settings: {
          ...defaultFilterSettings.settings,
          ...settings
        }
      });
    },

    resetCalculationMode() {
      this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
      MonthTimeReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
      MonthBudgetReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
    }
  },
};
</script>
